import { useMemo } from 'react';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import type { AppealFilters } from './appealFilterParams';

/**
 * CR-8206: the Submitted Appeals filters as the API should see them.
 *
 * The inputs keep showing every key as it is typed; the GRN, PO and part
 * number text reaches the list, the tab counts and the export only once the
 * user pauses, so typing sends one request instead of one per key. Status and
 * Supplier are picked from a list, so they apply at once.
 *
 * @example
 * const queryFilters = useDebouncedAppealFilters(appealFilters);
 * const params = buildAppealFilterParams(queryFilters);
 *
 * @param filters - The filter panel's live state
 * @param delayMs - Quiet period before typed text is applied (default 300ms)
 * @returns The filters to query with
 */
export function useDebouncedAppealFilters(filters: AppealFilters, delayMs = 300): AppealFilters {
  const typed = useMemo(
    () => ({ grn_number: filters.grn_number, po_number: filters.po_number, part_number: filters.part_number }),
    [filters.grn_number, filters.po_number, filters.part_number],
  );
  const debounced = useDebouncedValue(typed, delayMs);
  return useMemo(
    () => ({ ...debounced, status: filters.status, supplier: filters.supplier }),
    [debounced, filters.status, filters.supplier],
  );
}
